import { Card, Checkbox, Label, toast } from '@hemmafixa/ui';
import type { NotificationPreferences } from '@hemmafixa/shared';
import { useCurrentUser } from '@/features/auth/api';
import { useI18n } from '@/i18n/context';
import { QueryView } from '../shared/query-view';
import { useNotificationPreferences, useUpdateNotificationPreferences } from './api';

/** Email and SMS on or off. In-app notifications are always on. Saved as soon as a box changes. */
export function NotificationSettings() {
  const { t } = useI18n();
  const n = t.notifications;
  const user = useCurrentUser();
  const query = useNotificationPreferences();
  const update = useUpdateNotificationPreferences();

  const change = (key: keyof NotificationPreferences, value: boolean) =>
    update.mutate(
      { [key]: value },
      { onSuccess: () => toast.success(n.savedToast), onError: () => toast.error(n.saveFailed) },
    );

  return (
    <Card className="grid gap-4 p-5 sm:p-6">
      <div className="grid gap-1">
        <h2 className="font-semibold">{n.settingsTitle}</h2>
        <p className="text-sm text-muted-foreground">{n.settingsText}</p>
      </div>
      <QueryView query={query} loading="detail">
        {(preferences) => (
          <div className="grid gap-3">
            <div className="flex items-start gap-2">
              <Checkbox
                id="notify-email"
                checked={preferences.emailEnabled}
                disabled={update.isPending}
                onCheckedChange={(checked) => change('emailEnabled', checked === true)}
              />
              <Label htmlFor="notify-email" className="grid gap-0.5 font-normal">
                {n.email}
                <span className="text-xs text-muted-foreground">{user.email}</span>
              </Label>
            </div>
            <div className="flex items-start gap-2">
              <Checkbox
                id="notify-sms"
                checked={preferences.smsEnabled}
                disabled={update.isPending}
                onCheckedChange={(checked) => change('smsEnabled', checked === true)}
              />
              <Label htmlFor="notify-sms" className="grid gap-0.5 font-normal">
                {n.sms}
                <span className="text-xs text-muted-foreground">{user.phone ?? n.smsNeedsPhone}</span>
              </Label>
            </div>
          </div>
        )}
      </QueryView>
    </Card>
  );
}
